import React, { useEffect, useState, useCallback } from 'react';
import { doc, deleteDoc, onSnapshot, collection, query, orderBy } from 'firebase/firestore';
import { Trash2 } from 'lucide-react';
import { db } from './firebase';

/**
 * InterviewHistoryCard (JavaScript)
 *
 * Props:
 *  - session: optional, full session object (preferred)
 *  - sessionId: optional, Firestore doc id (if session not provided)
 *  - db: firebase firestore instance (required when using sessionId)
 *  - appId, userId: used when using sessionId mode
 *  - onView(session): callback when card clicked
 *  - onDeleted(sessionId): callback after successful delete
 *  - className: optional additional classes
 */
export function InterviewHistoryCard({
  session,
  sessionId,
  appId,
  userId,
  onView,
  onDeleted,
  className = ''
}) {
  const [localSession, setLocalSession] = useState(session || null);
  const [isLoading, setIsLoading] = useState(!session && !!sessionId);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If caller provided `session` we don't subscribe
    if (session) {
      setLocalSession(session);
      setIsLoading(false);
      return;
    }

    if (!sessionId || !db || !appId || !userId) {
      setError('Missing props to fetch session. Provide `session` or (`sessionId`,`db`,`appId`,`userId`).');
      setIsLoading(false);
      return;
    }

    const docRef = doc(db, `artifacts/${appId}/users/${userId}/interview_sessions`, sessionId);
    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) setLocalSession({ id: snap.id, ...snap.data() });
        else setLocalSession(null);
        setIsLoading(false);
      },
      (err) => {
        console.error('InterviewHistoryCard snapshot error:', err);
        setError('Could not load session.');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [session, sessionId, db, appId, userId]);

  const handleDelete = useCallback(
    async (e) => {
      e.stopPropagation();
      if (!window.confirm('Are you sure you want to delete this session history?')) return;

      try {
        const idToDelete = (localSession && localSession.id) || sessionId;
        if (!idToDelete) throw new Error('No session id to delete.');
        if (!db || !appId || !userId) throw new Error('Missing db/appId/userId to delete.');

        const sessionDocRef = doc(db, `artifacts/${appId}/users/${userId}/interview_sessions`, idToDelete);
        await deleteDoc(sessionDocRef);
        if (onDeleted) onDeleted(idToDelete);
      } catch (err) {
        console.error('Error deleting session:', err);
        setError(err.message || 'Could not delete session.');
      }
    },
    [localSession, sessionId, db, appId, userId, onDeleted]
  );

  if (isLoading) {
    return (
      <div className={`aspect-[3/4] rounded-xl p-4 bg-gray-800 border border-gray-700 ${className}`}>
        <p className="text-sm text-gray-400">Loading...</p>
      </div>
    );
  }

  if (!localSession) {
    return (
      <div className={`aspect-[3/4] rounded-xl p-4 bg-gray-800 border border-gray-700 ${className}`}>
        <p className="text-sm text-gray-400">Session not found.</p>
      </div>
    );
  }

  const previewQuestions = (localSession.qna_list || []).slice(0, 2);
  const dateText =
    localSession.createdAt && localSession.createdAt.seconds
      ? new Date(localSession.createdAt.seconds * 1000).toLocaleDateString()
      : '';

  return (
    <div
      onClick={() => onView && onView(localSession)}
      className={`group relative aspect-[3/4] rounded-xl shadow-sm border border-gray-700 p-4 flex flex-col justify-between hover:shadow-lg transition-shadow cursor-pointer ${className}`}
    >
      <div>
        <h3 className="font-bold text-white line-clamp-2">{localSession.title || 'Untitled session'}</h3>

        {previewQuestions.length > 0 ? (
          <ul className="mt-2 text-sm text-gray-400 list-disc list-inside space-y-1">
            {previewQuestions.map((item, idx) => (
              <li key={idx} className="truncate">
                {item.question}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 mt-2">No summary available.</p>
        )}
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-xs text-gray-500">{dateText}</div>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
          <button
            onClick={handleDelete}
            className="p-1.5 rounded-full hover:bg-red-500/20 text-gray-400 hover:text-red-400"
            aria-label="Delete session"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-400 mt-2">{error}</div>}
    </div>
  );
}

/**
 * useInterviewSessions
 * Subscribe to the user's interview_sessions collection
 * returns { sessions, isLoading, error }
 */
export function useInterviewSessions(db, appId, userId) {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!db || !appId || !userId) {
      setError('Missing db/appId/userId');
      setIsLoading(false);
      return;
    }

    const q = query(collection(db, `artifacts/${appId}/users/${userId}/interview_sessions`), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const history = [];
        querySnapshot.forEach((d) => history.push({ id: d.id, ...d.data() }));
        setSessions(history);
        setIsLoading(false);
      },
      (err) => {
        console.error('useInterviewSessions error:', err);
        setError('Could not load interview sessions.');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, appId, userId]);

  return { sessions, isLoading, error };
}

export default InterviewHistoryCard;
